'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Gem, HelpCircle, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppModeConfig } from '@/lib/use-app-mode';
import { useLeaderboardModal } from '@/contexts/leaderboard-modal-context';
import { CornerBrackets } from './ui/CornerBrackets';
import { FiligreeDivider } from './ui/FiligreeDivider';
import type { SkyboxTheme } from './theming/skyboxes/types';
import { TakeoverWordmark } from './ui/TakeoverIcon';
import { PortfolioDropdown } from './nav/PortfolioDropdown';
import { NotificationsDropdown } from './nav/NotificationsDropdown';
import { TileValueNav } from './nav/TileValueNav';
import { getTotalTileValue } from './nav/TileManagementModal';
import { RewardsSheet, getClaimableTotals } from './nav/RewardsSheet';
import { formatCompactUsd } from './data/mock-prices';
import { useLaunchTransition } from './launch/LaunchTransitionProvider';
import { useLobbyHeaderStats } from '@/hooks/use-lobby-header-stats';
import { useWallet } from '@/hooks/use-wallet';
interface LobbyNavProps {
  /** `null` when the active board's creator picked "None" in the wizard.
   *  Currently unused by the nav body (kept as a future hook for sky-aware
   *  HUD trims) but typed to match `LobbyClient`'s widened state. */
  skybox: SkyboxTheme | null;
  onSkyboxChange: (s: SkyboxTheme | null) => void;
  /** Re-opens the player onboarding tour. Hidden when not provided. */
  onReplayTour?: () => void;
  /** Hoisted rewards-sheet state so the mobile bottom nav can open
   *  the same panel as the header Rewards pill. Optional for
   *  backwards compatibility — falls back to local state. */
  rewardsOpen?: boolean;
  onRewardsOpenChange?: (v: boolean) => void;
}

type NavAction = 'lobby' | 'launch' | 'tutorial' | 'burn';

export function LobbyNav({
  onReplayTour,
  rewardsOpen: controlledRewardsOpen,
  onRewardsOpenChange,
}: LobbyNavProps) {
  const { openLeaderboard, isOpen: leaderboardOpen } = useLeaderboardModal();
  const { begin: beginLaunchTransition } = useLaunchTransition();
  const pathname = usePathname();
  const router = useRouter();

  // Nav items are derived from the current pathname so the `active`
  // highlight tracks the route the viewer is actually on — previously
  // "Lobby" was hard-coded `active: true`, which made the marketing
  // landing (`/`) light it up too. The `href` on Lobby + Create means
  // both items remain navigable even when this nav is rendered outside
  // `(lobby)/layout.tsx` (e.g. on the landing) where the launch-
  // transition provider isn't mounted.
  const navItems: Array<{
    label: string;
    action: NavAction;
    active: boolean;
    href?: string;
  }> = [
    {
      label: 'Lobby',
      action: 'lobby',
      href: '/lobby',
      active:
        pathname === '/lobby' ||
        (pathname?.startsWith('/board/') ?? false),
    },
    {
      label: 'Create',
      action: 'launch',
      href: '/create',
      active: pathname === '/create',
    },
    {
      label: 'Tutorial',
      action: 'tutorial',
      href: '/tutorial',
      active: pathname === '/tutorial',
    },
    {
      label: 'Burn',
      action: 'burn',
      href: '/burn',
      active: pathname === '/burn',
    },
  ];
  const liveStats = useLobbyHeaderStats();
  const { isConnected } = useWallet();
  // Tiles + Rewards pills only mount when connected — guests shouldn't
  // see portfolio chrome before they link a wallet.
  const fallback = getTotalTileValue();
  const tileTotal = liveStats.isLive ? liveStats.tileValueUsd : fallback.total;
  const coinSymbol = fallback.coinSymbol;
  const tileAlert = liveStats.isLive
    ? {
        risk: liveStats.tileAlertRisk,
        count: liveStats.tileAlertCount,
        minRunwayDays: liveStats.tileAlertMinRunwayDays,
      }
    : { risk: 'safe' as const, count: 0, minRunwayDays: 0 };
  const claimableFallback = getClaimableTotals();
  const claimableUsd = liveStats.isLive
    ? (liveStats.claimableUsd ?? 0)
    : claimableFallback.total;
  const claimableCount = liveStats.isLive
    ? liveStats.claimableCount
    : claimableFallback.count;
  const [uncontrolledRewardsOpen, setUncontrolledRewardsOpen] = useState(false);
  const rewardsOpen = controlledRewardsOpen ?? uncontrolledRewardsOpen;
  const setRewardsOpen = (v: boolean) => {
    if (onRewardsOpenChange) onRewardsOpenChange(v);
    else setUncontrolledRewardsOpen(v);
  };
  const { showLeaderboard } = useAppModeConfig();

  return (
    <div
      // Marker consumed by `globals.css` so the tutorial can grey
      // the lobby chrome out (opacity + grayscale, plus
      // `cursor: not-allowed`) without coupling the styles to a
      // class name. The input guard already blocks clicks here —
      // this is purely a visual cue so the bar doesn't *look*
      // interactive while the tutorial is running.
      data-lobby-header
      className={cn(
        'relative hidden lg:flex items-center gap-3 px-4 h-10 shrink-0',
        'bg-gradient-to-b from-[#0a0907]/95 to-[#050404]/95',
        'border-b border-amber-500/25 backdrop-blur-md',
        'shadow-[0_10px_40px_-30px_rgba(255,215,120,0.4)]',
      )}
    >
      <CornerBrackets size={10} />

      <div className="flex items-center gap-2 pr-3 border-r border-amber-500/15">
        <Link
          href="/"
          className="inline-flex min-w-0 rounded-[3px] text-left transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/70"
          aria-label="Takeover home"
        >
          <TakeoverWordmark size={11} />
        </Link>
        <FiligreeDivider direction="right" width={22} className="opacity-60" />
      </div>

      <nav className="flex items-center gap-0.5">
        {navItems.map((item) => {
          const className = cn(
            'relative px-3 h-7 inline-flex items-center justify-center text-[10px] tracking-[0.2em] font-mono uppercase rounded-[4px] transition-colors',
            item.active
              ? 'text-amber-100 bg-gradient-to-b from-amber-500/15 to-transparent'
              : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
          );
          const inner = (
            <>
              {item.label}
              {item.active && (
                <span
                  aria-hidden
                  className="absolute left-1 right-1 -bottom-px h-px bg-gradient-to-r from-transparent via-amber-300/80 to-transparent"
                />
              )}
            </>
          );
          // Every nav item is now a `<Link>` — Lobby, Create, and
          // Tutorial all carry an `href`, so the browser surfaces the
          // destination on hover and the cursor reads as a link.
          // Create additionally fires `beginLaunchTransition` on click:
          // inside `(lobby)/layout.tsx` the provider plays the entrance
          // choreography (and pushes `/create` itself, dedup'd by Next
          // against the Link's own push); outside the provider the
          // hook is a safe no-op and the Link's normal navigation
          // takes over.
          return (
            <Link
              key={item.label}
              href={item.href ?? '#'}
              onClick={
                item.action === 'launch'
                  ? (event) =>
                      beginLaunchTransition({
                        clientX: event.clientX,
                        clientY: event.clientY,
                      })
                  : undefined
              }
              className={className}
            >
              {inner}
            </Link>
          );
        })}
        {showLeaderboard ? (
          <button
            type="button"
            onClick={() => openLeaderboard()}
            aria-pressed={leaderboardOpen}
            className={cn(
              'relative ml-0.5 px-3 h-7 flex items-center gap-1.5 text-[10px] tracking-[0.2em] font-mono uppercase rounded-[4px] transition-colors',
              leaderboardOpen
                ? 'text-amber-100 bg-gradient-to-b from-amber-500/15 to-transparent border border-amber-500/25'
                : 'text-zinc-400 hover:text-amber-100 hover:bg-white/5 border border-transparent hover:border-amber-500/20',
            )}
          >
            <Trophy className="h-3 w-3" aria-hidden />
            Leaders
          </button>
        ) : null}
      </nav>

      <div className="flex-1" />

      {isConnected && (
        <>
          {/* `tileValuePending` is set when at least one TAKEOVER seat
              was excluded from `tileValueUsd` because the price oracle
              hasn't ticked yet. Showing the skeleton in that window
              avoids the chip flashing a partial total (or, before the
              `useLobbyHeaderStats` patch, a ×10¹² inflated number from
              raw 18-dp TAKEOVER bigints leaking into the USD-6dp
              accumulator). */}
          {liveStats.isLoading || liveStats.tileValuePending ? (
            <TileValuePillSkeleton />
          ) : (
            <TileValueNav
              tileValue={tileTotal.toFixed(2)}
              coinSymbol={coinSymbol}
              alertRisk={tileAlert.risk}
              alertCount={tileAlert.count}
              minRunwayDays={tileAlert.minRunwayDays}
              showConnectWallet={false}
            />
          )}

          <button
            type="button"
            onClick={() => setRewardsOpen(true)}
            aria-label="Open rewards"
            aria-pressed={rewardsOpen}
            title={
              liveStats.isLoading
                ? 'Rewards · loading'
                : `Rewards · ${formatCompactUsd(claimableUsd)}`
            }
            disabled={liveStats.isLoading}
            className={cn(
              'relative h-7 pl-2 pr-2.5 flex items-center gap-1.5 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em] transition-colors',
              'bg-gradient-to-b from-emerald-500/15 to-emerald-500/5 border-emerald-400/40 text-emerald-100 hover:border-emerald-300/70 hover:from-emerald-500/25',
              liveStats.isLoading && 'opacity-70 cursor-wait',
            )}
          >
            <Gem className="h-3 w-3 text-emerald-300" />
            {liveStats.isLoading ? (
              <span
                aria-hidden
                className="inline-block h-2.5 w-10 rounded-sm bg-emerald-400/20 animate-pulse"
              />
            ) : (
              <span className="tabular-nums">{formatCompactUsd(claimableUsd)}</span>
            )}
            {!liveStats.isLoading && claimableCount > 0 && (
              <span
                aria-hidden
                className="absolute -top-0.5 -right-0.5 h-3.5 min-w-[14px] px-1 grid place-items-center rounded-full bg-emerald-500 text-black text-[8px] font-bold font-mono shadow-[0_0_8px_rgba(16,185,129,0.7)]"
              >
                {claimableCount}
              </span>
            )}
          </button>

          <RewardsSheet open={rewardsOpen} onOpenChange={setRewardsOpen} />
        </>
      )}

      {onReplayTour && (
        <button
          type="button"
          onClick={onReplayTour}
          aria-label="Replay player tour"
          title="Replay player tour"
          className={cn(
            'relative h-7 w-7 flex items-center justify-center rounded-[4px] border transition-colors',
            'bg-black/30 hover:bg-white/5 border-amber-500/25 text-zinc-300 hover:text-amber-100',
          )}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      )}

      <NotificationsDropdown />

      <PortfolioDropdown />
    </div>
  );
}

/**
 * Placeholder for the Tile value pill while `useLobbyHeaderStats` refetches
 * the new wallet's summary. Mirrors the `TileValueNav` chrome (h-7, amber
 * gradient, LayoutGrid-sized icon slot) so the switch feels like the value
 * is loading in place, not like the pill disappeared.
 */
function TileValuePillSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading tile value"
      className={cn(
        'relative h-7 pl-2 pr-2.5 flex items-center gap-1.5 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em]',
        'bg-gradient-to-b from-amber-500/10 to-amber-500/5 border-amber-400/30 text-amber-200/60',
      )}
    >
      <span
        aria-hidden
        className="inline-block h-3 w-3 rounded-sm bg-amber-400/20 animate-pulse"
      />
      <span className="hidden sm:inline text-zinc-500">Tiles</span>
      <span
        aria-hidden
        className="inline-block h-2.5 w-10 rounded-sm bg-amber-400/20 animate-pulse"
      />
    </div>
  );
}
